import { Mp3Encoder } from "@breezystack/lamejs";

const floatTo16BitPCM = (input, startIndex, endIndex) => {
  const length = endIndex - startIndex;
  const output = new Int16Array(length);
  for (let i = 0; i < length; i++) {
    const sample = Math.max(-1, Math.min(1, input[startIndex + i]));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
};

// Encode an AudioBuffer slice [startSec, endSec] to a 128 kbps mp3 Blob in the
// browser, so we never upload the (potentially large) raw file to the server.
export const encodeBufferToMp3 = (buffer, startSec, endSec) => {
  const sampleRate = buffer.sampleRate;
  const channels = Math.min(buffer.numberOfChannels, 2);

  const startIndex = Math.max(0, Math.floor(startSec * sampleRate));
  const endIndex = Math.min(buffer.length, Math.floor(endSec * sampleRate));

  const left = floatTo16BitPCM(buffer.getChannelData(0), startIndex, endIndex);
  const right =
    channels > 1
      ? floatTo16BitPCM(buffer.getChannelData(1), startIndex, endIndex)
      : null;

  const encoder = new Mp3Encoder(channels, sampleRate, 128);
  const blockSize = 1152;
  const chunks = [];

  for (let i = 0; i < left.length; i += blockSize) {
    const leftChunk = left.subarray(i, i + blockSize);
    const mp3buf =
      channels > 1
        ? encoder.encodeBuffer(leftChunk, right.subarray(i, i + blockSize))
        : encoder.encodeBuffer(leftChunk);
    if (mp3buf.length > 0) {
      chunks.push(new Uint8Array(mp3buf));
    }
  }

  const end = encoder.flush();
  if (end.length > 0) {
    chunks.push(new Uint8Array(end));
  }

  return new Blob(chunks, { type: "audio/mp3" });
};

export const isWav = (file) =>
  /\.wav$/i.test(file.name) ||
  file.type === "audio/wav" ||
  file.type === "audio/x-wav";

export const isMp3 = (file) =>
  /\.mp3$/i.test(file.name) ||
  file.type === "audio/mp3" ||
  file.type === "audio/mpeg";

// Decode any browser-supported audio file (WAV, M4A/AAC, etc.) and re-encode it
// to a 128 kbps mp3 File, keeping the base name. This both avoids uploading a
// large uncompressed original and normalizes formats that the backend
// watermarker's libsndfile can't decode (e.g. M4A/AAC).
export const convertToMp3 = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const buffer = await ctx.decodeAudioData(arrayBuffer);
    const blob = encodeBufferToMp3(buffer, 0, buffer.duration);
    const base = file.name.replace(/\.[^/.]+$/, "") || "audio";
    return new File([blob], `${base}.mp3`, { type: "audio/mp3" });
  } finally {
    ctx.close();
  }
};

// Backwards-compatible alias for existing callers.
export const convertWavToMp3 = convertToMp3;
